import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { User, Lock, Download, Trash2, Link2 } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { supabase } from '@/lib/supabase';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import { ShareProfileManager } from '@/components/sharing/ShareProfileManager';

const profileSchema = z.object({
  display_name: z.string().min(1, 'Name ist erforderlich'),
  email: z.string().email('Ungültige E-Mail-Adresse'),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(6, 'Mindestens 6 Zeichen'),
  newPassword: z.string().min(6, 'Mindestens 6 Zeichen'),
  confirmPassword: z.string().min(6, 'Mindestens 6 Zeichen'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Passwörter stimmen nicht überein',
  path: ['confirmPassword'],
});

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

export default function Settings() {
  const { user, profile, updateProfile, signOut } = useAuthStore();
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isLoadingPassword, setIsLoadingPassword] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { register: registerProfile, handleSubmit: handleSubmitProfile, formState: { errors: profileErrors } } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      display_name: profile?.display_name || '',
      email: user?.email || '',
    },
  });

  const { register: registerPassword, handleSubmit: handleSubmitPassword, reset: resetPassword, formState: { errors: passwordErrors } } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  const onSubmitProfile = async (data: ProfileFormData) => {
    setIsLoadingProfile(true);
    try {
      await updateProfile({ display_name: data.display_name });
      toast.success('Profil erfolgreich aktualisiert');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Aktualisieren';
      toast.error('Fehler beim Aktualisieren', { description: message });
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const onSubmitPassword = async (data: PasswordFormData) => {
    setIsLoadingPassword(true);
    try {
      // Verify current password by trying to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: data.currentPassword,
      });

      if (signInError) {
        throw new Error('Aktuelles Passwort ist falsch');
      }

      // Update password
      const { error } = await supabase.auth.updateUser({
        password: data.newPassword,
      });

      if (error) throw error;

      toast.success('Passwort erfolgreich geändert');
      resetPassword();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Ändern des Passworts';
      toast.error('Fehler beim Ändern des Passworts', { description: message });
    } finally {
      setIsLoadingPassword(false);
    }
  };

  const handleExportData = async () => {
    try {
      // Fetch all user data
      const { data: properties, error } = await supabase
        .from('properties')
        .select(`*, financings(*), property_images(*)`);

      if (error) throw error;

      // Create JSON export
      const exportData = {
        profile,
        properties,
        exportedAt: new Date().toISOString(),
      };

      // Download as JSON
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fundament-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Daten erfolgreich exportiert');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Exportieren';
      toast.error('Fehler beim Exportieren', { description: message });
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      // Delete all user's properties (cascade will handle related data)
      const { error: deleteError } = await supabase
        .from('properties')
        .delete()
        .eq('owner_id', user?.id);

      if (deleteError) throw deleteError;

      // Delete profile
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', user?.id);

      if (profileError) throw profileError;

      toast.success('Account erfolgreich gelöscht');
      await signOut();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Löschen';
      toast.error('Fehler beim Löschen', { description: message });
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-semibold">Einstellungen</h1>
        <p className="text-muted-foreground mt-2">Verwalte dein Profil und Account-Einstellungen</p>
      </div>

      {/* Profile Section */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-primary/10">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Profil</h2>
            <p className="text-sm text-muted-foreground">Aktualisiere deine persönlichen Informationen</p>
          </div>
        </div>
        <form onSubmit={handleSubmitProfile(onSubmitProfile)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="display_name">Name</Label>
            <Input
              id="display_name"
              {...registerProfile('display_name')}
              error={profileErrors.display_name?.message}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">E-Mail</Label>
            <Input
              id="email"
              type="email"
              {...registerProfile('email')}
              disabled
              className="opacity-60 cursor-not-allowed"
            />
            <p className="text-xs text-muted-foreground">E-Mail-Adresse kann nicht geändert werden</p>
          </div>
          <Button type="submit" isLoading={isLoadingProfile}>
            Profil speichern
          </Button>
        </form>
      </Card>

      {/* Password Section */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-primary/10">
            <Lock className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Passwort ändern</h2>
            <p className="text-sm text-muted-foreground">Aktualisiere dein Passwort</p>
          </div>
        </div>
        <form onSubmit={handleSubmitPassword(onSubmitPassword)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="currentPassword">Aktuelles Passwort</Label>
            <Input
              id="currentPassword"
              type="password"
              {...registerPassword('currentPassword')}
              error={passwordErrors.currentPassword?.message}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="newPassword">Neues Passwort</Label>
            <Input
              id="newPassword"
              type="password"
              {...registerPassword('newPassword')}
              error={passwordErrors.newPassword?.message}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
            <Input
              id="confirmPassword"
              type="password"
              {...registerPassword('confirmPassword')}
              error={passwordErrors.confirmPassword?.message}
            />
          </div>
          <Button type="submit" isLoading={isLoadingPassword}>
            Passwort ändern
          </Button>
        </form>
      </Card>

      {/* Data Export Section */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-primary/10">
            <Download className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Daten exportieren</h2>
            <p className="text-sm text-muted-foreground">Lade eine Kopie deiner Daten herunter</p>
          </div>
        </div>
        <Button onClick={handleExportData} variant="secondary">
          Als JSON exportieren
        </Button>
      </Card>

      {/* Share Profiles Section */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-primary/10">
            <Link2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Freigabe-Profile</h2>
            <p className="text-sm text-muted-foreground">Vorlagen für wiederverwendbare Freigabe-Links</p>
          </div>
        </div>
        <ShareProfileManager />
      </Card>

      {/* Danger Zone */}
      <Card className="p-6 border-destructive/50">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-destructive/10">
            <Trash2 className="h-5 w-5 text-destructive" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-destructive">Gefahrenzone</h2>
            <p className="text-sm text-muted-foreground">Unwiderrufliche Aktionen</p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-4">
              Das Löschen deines Accounts ist permanent. Alle deine Immobilien, Finanzierungen und Bilder werden gelöscht.
            </p>
            <Button onClick={() => setDeleteModalOpen(true)} variant="destructive">
              Account löschen
            </Button>
          </div>
        </div>
      </Card>

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteAccount}
        title="Account löschen"
        description="Diese Aktion kann nicht rückgängig gemacht werden. Alle deine Daten werden permanent gelöscht."
        isLoading={isDeleting}
      />
    </div>
  );
}
